import { useCallback, useEffect, useState } from 'react';

import type { UseSidePanelOrderResult } from '@/features/patient-record/components/panels/types';

const STORAGE_KEY = 'ptv2:patient:sidePanelOrder';

function readPersisted(): readonly string[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((value): value is string => typeof value === 'string');
  } catch {
    return null;
  }
}

function writePersisted(order: readonly string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  } catch {
    // Ignore storage errors (private mode, quota).
  }
}

/**
 * Reconciles a persisted order list with the current set of available
 * panel ids: known ids appear in their saved position, unknown ids are
 * dropped, and any newly registered ids are appended to the end. Same
 * shape returned each call when the inputs are stable so React state
 * doesn't churn.
 */
function reconcile(
  persisted: readonly string[] | null,
  ids: readonly string[],
): readonly string[] {
  const known = new Set(ids);
  const seen = new Set<string>();
  const merged: string[] = [];
  if (persisted !== null) {
    for (const id of persisted) {
      if (known.has(id) && !seen.has(id)) {
        merged.push(id);
        seen.add(id);
      }
    }
  }
  for (const id of ids) {
    if (!seen.has(id)) {
      merged.push(id);
      seen.add(id);
    }
  }
  return merged;
}

/**
 * Persists the patient side-rail panel order in localStorage so each
 * user sees their preferred arrangement on return. `move` reorders by
 * dropping the source onto the target's slot; `nudge` shifts a panel up
 * or down by one position (keyboard accessible reorder).
 */
export function useSidePanelOrder(
  ids: readonly string[],
): UseSidePanelOrderResult {
  const [order, setOrder] = useState<readonly string[]>(() =>
    reconcile(readPersisted(), ids),
  );

  useEffect(() => {
    setOrder((prev) => {
      const next = reconcile(prev, ids);
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) {
        return prev;
      }
      return next;
    });
  }, [ids]);

  useEffect(() => {
    writePersisted(order);
  }, [order]);

  const move = useCallback((sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    setOrder((prev) => {
      const fromIdx = prev.indexOf(sourceId);
      const toIdx = prev.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, sourceId);
      return next;
    });
  }, []);

  const nudge = useCallback((sourceId: string, delta: -1 | 1) => {
    setOrder((prev) => {
      const idx = prev.indexOf(sourceId);
      if (idx === -1) return prev;
      const targetIdx = idx + delta;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      if (removed === undefined) return prev;
      next.splice(targetIdx, 0, removed);
      return next;
    });
  }, []);

  return { order, move, nudge };
}
